import { $logger } from '@/boot/logger'
import { $pegApi } from '@/boot/peg-api'
import { MEDIA_TYPES } from '@/constants/appReturn.const'
import { useCustomerReturnPayloadBuilder } from '@/composables/useCustomerReturnPayloadBuilder'
import { useCustomerReturnErrorHandler } from '@/composables/useCustomerReturnErrorHandler'

/**
 * @typedef {Object} ConfirmationData
 * @property {Array} returnItems - Items being returned for refund
 * @property {Array} auctionItems - Items being sent to auction
 * @property {Array} inStoreCredits - In-store credit amounts
 */

/**
 * Customer Return Submission Store
 *
 * Manages the final submission of return data and media uploads.
 * This store handles:
 * - Return submission to backend API
 * - Confirmation data storage
 * - Media uploads (photos, damage images, etc.)
 * - Submission state management
 *
 * @public API:
 * - State: confirmation, done, loading
 * - Computed: isSubmitted, hasConfirmation, totalConfirmedItems, isSubmitting, isUploadingMedia
 * - Actions: submitReturn, uploadMedia, clearConfirmation, reset
 *
 * @store customerReturnSubmission
 */

// ============================================================================
// PRIVATE HELPER FUNCTIONS
// ============================================================================

/**
 * Validate items to process before submission
 * @param {any} itemsToProcess - Items to validate
 * @param {import('@/boot/logger').Logger} logger - Logger instance
 * @returns {boolean} True if valid
 * @throws {Error} If validation fails
 * @private
 */
function _validateItemsToProcess(itemsToProcess, logger) {
  if (!Array.isArray(itemsToProcess) || itemsToProcess.length === 0) {
    logger.error('Invalid or empty items to process', { itemsToProcess })
    throw new Error('No items to submit')
  }
  return true
}

/**
 * Prepare media object from upload response
 * @param {Object} returnSessionMedia - Media data from API
 * @param {string} mediaType - Type of media
 * @param {string} sourceImage - Original base64 image
 * @returns {Object} Formatted media object
 * @private
 */
function _buildMediaObject(returnSessionMedia, mediaType, sourceImage) {
  return {
    id: returnSessionMedia.id,
    mediaType,
    image: returnSessionMedia.url,
    src: sourceImage,
    // Pass through validation fields from server response
    isValid: returnSessionMedia.isValid ?? null,
    error: returnSessionMedia.error ?? null,
    imageDescription: returnSessionMedia.imageDescription ?? null,
  }
}

// ============================================================================
// STORE DEFINITION
// ============================================================================

export const useCustomerReturnSubmissionStore = defineStore('customerReturnSubmission', () => {
  // Composables
  const { buildSubmissionPayload } = useCustomerReturnPayloadBuilder()
  const { handleSubmissionError, handleMediaUploadError } = useCustomerReturnErrorHandler()

  // ============================================================================
  // STATE
  // ============================================================================

  /**
   * Confirmation data from successful return submission
   * @type {Ref<ConfirmationData>}
   * @public
   */
  const confirmation = ref({
    returnItems: [],
    auctionItems: [],
    inStoreCredits: [],
  })

  /**
   * Whether the entire return process is complete
   * @type {Ref<boolean>}
   * @public
   */
  const done = ref(false)

  /**
   * Loading states for submission operations
   * @type {Ref<Object>}
   * @public
   */
  const loading = ref({
    submission: false,
    mediaUpload: false,
  })

  // ============================================================================
  // COMPUTED PROPERTIES
  // ============================================================================

  /**
   * Whether submission has completed successfully
   * @returns {boolean}
   * @public
   */
  const isSubmitted = computed(() => done.value)

  /**
   * Whether there is confirmation data available
   * @returns {boolean}
   * @public
   */
  const hasConfirmation = computed(() => {
    return (
      confirmation.value.returnItems.length > 0 ||
      confirmation.value.auctionItems.length > 0 ||
      confirmation.value.inStoreCredits.length > 0
    )
  })

  /**
   * Total number of items in confirmation
   * @returns {number}
   * @public
   */
  const totalConfirmedItems = computed(() => {
    return (
      confirmation.value.returnItems.length + confirmation.value.auctionItems.length + confirmation.value.inStoreCredits.length
    )
  })

  /**
   * Whether currently submitting
   * @returns {boolean}
   * @public
   */
  const isSubmitting = computed(() => loading.value.submission)

  /**
   * Whether currently uploading media
   * @returns {boolean}
   * @public
   */
  const isUploadingMedia = computed(() => loading.value.mediaUpload)

  // ============================================================================
  // ACTIONS (PUBLIC & PRIVATE)
  // ============================================================================

  /**
   * Prepare and validate items for submission
   * @param {Array} itemsToProcess - Items from processing queue (with per-item assessments)
   * @param {Object} wrongItemsById - Wrong item data by ID
   * @returns {Array} Mapped payload items
   * @private - Used internally by submitReturn
   */
  function prepareSubmission(itemsToProcess, wrongItemsById) {
    _validateItemsToProcess(itemsToProcess, $logger)

    // Build payload using composable (items now include their own assessments)
    const mappedItems = buildSubmissionPayload(itemsToProcess, wrongItemsById || {})

    return mappedItems
  }

  /**
   * Submit return to backend API
   * @param {Object} params - Submission parameters
   * @param {string} params.retailerId - Retailer ID
   * @param {string} params.orderNumber - Order number
   * @param {string} params.returnSessionId - Return session ID
   * @param {Array} params.items - Mapped items for submission
   * @returns {Promise<Object>} API response with confirmation data
   * @private - Used internally by submitReturn
   */
  async function submitToBackend({ retailerId, orderNumber, returnSessionId, items }) {
    const response = await $pegApi.Retailers.Orders.submitReturn(retailerId, orderNumber, {
      returnSessionId,
      items,
    })

    return response
  }

  /**
   * Store confirmation data from API response
   * @param {Object} response - API response
   * @param {Array} response.returnItems - Items being returned
   * @param {Array} response.auctionItems - Items going to auction
   * @param {Array} response.inStoreCredits - Store credit amounts
   * @private - Used internally by submitReturn
   */
  function storeConfirmation(response) {
    confirmation.value.returnItems = response.returnItems || []
    confirmation.value.auctionItems = response.auctionItems || []
    confirmation.value.inStoreCredits = response.inStoreCredits || []
  }

  /**
   * Complete return submission flow
   * @param {Object} params - Submission parameters
   * @param {string} params.retailerId - Retailer ID
   * @param {string} params.orderNumber - Order number
   * @param {string} params.returnSessionId - Return session ID
   * @param {Array} params.itemsToProcess - Items from processing queue (with per-item assessments)
   * @param {Object} params.wrongItemsById - Wrong item data
   * @returns {Promise<boolean>} Success status
   * @public
   */
  async function submitReturn({ retailerId, orderNumber, returnSessionId, itemsToProcess, wrongItemsById }) {
    try {
      loading.value.submission = true

      // Prepare submission payload (items include their own packaging/product state assessments)
      const mappedItems = prepareSubmission(itemsToProcess, wrongItemsById)

      // Submit to backend
      const response = await submitToBackend({
        retailerId,
        orderNumber,
        returnSessionId,
        items: mappedItems,
      })

      // Store confirmation
      storeConfirmation(response)

      // Mark as done
      done.value = true

      return true
    } catch (error) {
      handleSubmissionError(error, {
        retailerId,
        orderNumber,
        itemCount: itemsToProcess?.length || 0,
      })
      done.value = false
      return false
    } finally {
      loading.value.submission = false
    }
  }

  /**
   * Upload media to the server
   * @param {string} mediaType - Type of media (BOX, PRODUCT, DAMAGE)
   * @param {string} image - Base64 image data
   * @returns {Promise<Object>} Media object with id, mediaType, image URL, and source
   * @throws {Error} When upload fails
   * @public
   */
  async function uploadMedia(mediaType, image) {
    try {
      loading.value.mediaUpload = true

      const { returnSessionMedia } = await $pegApi.ReturnSessionMedia.add({
        mediaType,
        image,
        description: mediaType === MEDIA_TYPES.DAMAGE ? 'damage' : null,
      })

      const mediaObject = _buildMediaObject(returnSessionMedia, mediaType, image)

      return mediaObject
    } catch (error) {
      handleMediaUploadError(error, mediaType)
      throw error
    } finally {
      loading.value.mediaUpload = false
    }
  }

  /**
   * Clear confirmation data
   * @public
   */
  function clearConfirmation() {
    confirmation.value.returnItems = []
    confirmation.value.auctionItems = []
    confirmation.value.inStoreCredits = []
  }

  /**
   * Reset submission state
   * @param {Object} options - Reset options
   * @param {boolean} options.clearConfirmation - Whether to clear confirmation data
   * @public
   */
  function reset({ clearConfirmation: shouldClearConfirmation = false } = {}) {
    done.value = false
    loading.value = {
      submission: false,
      mediaUpload: false,
    }

    if (shouldClearConfirmation) {
      clearConfirmation()
    }
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  return {
    // State
    confirmation,
    done,
    loading,
    // Computed
    isSubmitted,
    hasConfirmation,
    totalConfirmedItems,
    isSubmitting,
    isUploadingMedia,
    // Actions (only public methods exposed)
    submitReturn,
    uploadMedia,
    clearConfirmation,
    reset,
  }
})
